import cls from 'classnames';
import { useState, ChangeEvent, useEffect, useRef } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useWindowSize } from 'usehooks-ts';
import IconSearch from '@picsart/icons/IconSearch';
import IconCrossCircleOutile from '@picsart/icons/IconCrossCircleOutline';
import IconFilter from '@picsart/icons/IconFilter';

import { MENU_COLLAPSE_BREAKPOINT } from '../../../common/SingleToolNavigation';

import ISectionWithStickyInput from './types';
import useStyles, { DESKTOP_CMP_PADDING, MOBILE_CMP_PADDING } from './styles';

export default function SectionWithStickyInput({
  imgUrl,
  className,
  placeholder,
  primaryText,
  secondaryText,
  defaultValue,
  onChange,
  onInputClick,
  onFilterClick,
  onStickyOffsetChange,
  inputRightElement = null,
  belowInputElement = null,
  hasSearchIcon,
  autoFocus,
  hasMobileDescription = false,
  hasFilterButton = true,
  reviews,
  inputValue,
  hasCloseIcon,
}: ISectionWithStickyInput) {
  const containerRef = useRef(null);
  const styles = useStyles({ hasMobileDescription, hasFilterButton });
  const [value, setValue] = useState<string>(() => defaultValue || '');
  const inputRef = useRef<HTMLInputElement>(null);
  const { width, height } = useWindowSize();
  const [top, setTop] = useState(0);
  const containerHeight = containerRef.current?.getBoundingClientRect().height;
  const inputHeight = inputRef.current?.getBoundingClientRect().height;
  const [headerHeight, setHeaderHeight] = useState(0);

  const inputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value: v },
    } = e;
    setValue(v);
    onChange(v);
  };

  const onClearInput = () => {
    if (value) {
      setValue('');
      onChange('');
      inputRef?.current.focus();
    }
  };

  useEffect(() => {
    const header = document.querySelector('.header');

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === header) {
          setHeaderHeight(entry.contentRect.height);
        }
      }
    });

    resizeObserver.observe(header);

    setHeaderHeight(header.clientHeight);

    return () => {
      resizeObserver.unobserve(header);
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!containerHeight) {
      return;
    }
    let nextTop: number;

    if (width > MENU_COLLAPSE_BREAKPOINT) {
      nextTop =
        -containerHeight +
        2 * DESKTOP_CMP_PADDING -
        4 + // some offset for emojis & symbols
        headerHeight +
        inputHeight;
    } else {
      nextTop =
        -containerHeight +
        2 * MOBILE_CMP_PADDING +
        16 + // mobile padding
        headerHeight +
        inputHeight;
    }
    setTop(nextTop);
  }, [width, height, containerHeight, inputHeight, containerRef, headerHeight]);

  useEffect(() => {
    if (onStickyOffsetChange && containerHeight) {
      onStickyOffsetChange(top + containerHeight);
    }
  }, [top]);

  useEffect(() => {
    if (!inputValue) {
      return;
    }
    setValue(inputValue);
    onChange(inputValue);
  }, [inputValue]);

  useEffect(() => {
    autoFocus && inputRef.current.focus();
  }, []);

  return (
    <section ref={containerRef} className={cls(styles.root, className)} style={{ top }}>
      {imgUrl && (
        <div className={styles.imgContainer}>
          <LazyLoadImage className={styles.img} src={imgUrl} />
        </div>
      )}

      <div className={styles.container}>
        <div className={styles.textContainer}>
          <h1 className={styles.primaryText}>{primaryText}</h1>
          <p className={styles.secondaryText}>{secondaryText}</p>
        </div>
        {reviews}
        <div className={styles.inputContainer}>
          {hasSearchIcon && <IconSearch className={styles.magnifierIcon} />}
          <input
            id="sticky-search-input"
            className={cls(styles.input, !hasSearchIcon && styles.inputNoSearch)}
            placeholder={placeholder}
            onChange={inputChange}
            onClick={onInputClick}
            value={value}
            ref={inputRef}
            autoFocus={autoFocus}
          />
          {hasCloseIcon && value ? (
            <div className={styles.closeIconContainer} onClick={onClearInput}>
              <IconCrossCircleOutile className={styles.closeIcon} />
            </div>
          ) : (
            inputRightElement
          )}
          {hasFilterButton && (
            <div className={styles.filterContainer}>
              <button className={styles.filterButton} onClick={onFilterClick}>
                <IconFilter className={styles.filterIcon} />
              </button>
            </div>
          )}
        </div>
        {belowInputElement}
      </div>
    </section>
  );
}
